import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, doc, getDoc, setDoc, addDoc } from "firebase/firestore";
import { useAuth } from "./AuthProvider";
import { Box, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { saveAs } from 'file-saver';

const statusOptions = ["active", "inactive", "expired"];
const planOptions = ["demo","monthly", "yearly"];

const Admin = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState({});
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);
  const [history, setHistory] = useState({});
  const [showHistoryFor, setShowHistoryFor] = useState(null);
  const [scrapeHistory, setScrapeHistory] = useState({});
  const [showScrapeFor, setShowScrapeFor] = useState(null);
  const [expandedScrape, setExpandedScrape] = useState({});

  useEffect(() => {
    if (!currentUser) return;
    const checkAdmin = async () => {
      setAdminLoading(true);
      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists() && userSnap.data().role === "admin") {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
      setAdminLoading(false);
    };
    checkAdmin();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !isAdmin) return;
    const fetchUsers = async () => {
      setLoading(true);
      const usersSnap = await getDocs(collection(db, "users"));
      const userList = [];
      for (const userDoc of usersSnap.docs) {
        const userData = userDoc.data();
        const subRef = doc(db, "users", userDoc.id, "subscriptions", "active");
        const subSnap = await getDoc(subRef);
        // Fetch latest scrape history
        let latestScrape = { keyword: "", location: "" };
        const scrapeCol = collection(db, "users", userDoc.id, "scrape_history");
        const scrapeSnap = await getDocs(scrapeCol);
        if (!scrapeSnap.empty) {
          // Get the latest by scraped_at
          const sorted = scrapeSnap.docs
            .map(doc => doc.data())
            .sort((a, b) => new Date(b.scraped_at) - new Date(a.scraped_at));
          latestScrape = sorted[0];
        }
        userList.push({
          uid: userDoc.id,
          email: userData.email,
          subscription: subSnap.exists() ? subSnap.data() : {},
          latestScrape
        });
      }
      setUsers(userList);
      setLoading(false);
    };
    fetchUsers();
  }, [currentUser, isAdmin]);

  const handleChange = (uid, field, value) => {
    let updated = { ...editing[uid] };
    // Helper to format date as yyyy-mm-dd
    const formatDate = (date) => {
      return date.toISOString().slice(0, 10);
    };
    // Helper to add time
    const addPeriod = (start, plan) => {
      const d = new Date(start);
      if (plan === "demo") d.setDate(d.getDate() + 7);
      else if (plan === "monthly") d.setMonth(d.getMonth() + 1);
      else if (plan === "yearly") d.setFullYear(d.getFullYear() + 1);
      return formatDate(d);
    };

    if (field === "plan") {
      updated.plan = value;
      // Set tokens based on plan
      if (value === "demo") updated.tokens = 2;
      else if (value === "monthly") updated.tokens = 10;
      else if (value === "yearly") updated.tokens = 15;
      // Set start_date to today if not set
      let startDate = updated.start_date || formatDate(new Date());
      updated.start_date = startDate;
      // Set expiry_date based on plan
      updated.expiry_date = addPeriod(startDate, value);
    } else if (field === "start_date") {
      updated.start_date = value;
      // Update expiry_date based on plan
      if (updated.plan) {
        updated.expiry_date = addPeriod(value, updated.plan);
      }
    } else if (field === "tokens") {
      updated.tokens = Number(value);
    } else {
      updated[field] = value;
    }
    setEditing({
      ...editing,
      [uid]: updated
    });
  };

  const handleSave = async (uid) => {
    const subRef = doc(db, "users", uid, "subscriptions", "active");
    await setDoc(subRef, editing[uid], { merge: true });
    // Add a new history record
    const subsCol = collection(db, "users", uid, "subscriptions");
    const historyData = { ...editing[uid], saved_at: new Date().toISOString() };
    await addDoc(subsCol, historyData);
    setEditing({ ...editing, [uid]: undefined });
    window.location.reload();
  };

  const fetchHistory = async (uid) => {
    const subsCol = collection(db, "users", uid, "subscriptions");
    const subsSnap = await getDocs(subsCol);
    setHistory({
      ...history,
      [uid]: subsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    });
    setShowHistoryFor(uid);
  };

  const fetchScrapeHistory = async (uid) => {
    const scrapeCol = collection(db, "users", uid, "scrape_history");
    const scrapeSnap = await getDocs(scrapeCol);
    const allScrapes = scrapeSnap.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => new Date(b.scraped_at) - new Date(a.scraped_at));
    setScrapeHistory({ ...scrapeHistory, [uid]: allScrapes });
    setShowScrapeFor(uid);
  };

  function downloadCSVFromResults(results, filename = 'leads.csv') {
    if (!results || !results.length) return;
    const replacer = (key, value) => value === null ? '' : value;
    const header = Object.keys(results[0]);
    const csv = [
      header.join(','),
      ...results.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','))
    ].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  if (adminLoading) {
    return <Box p={4}><Typography>Checking admin status...</Typography></Box>;
  }
  if (!isAdmin) {
    return <Box p={4}><Typography>You are not authorized to view this page.</Typography></Box>;
  }

  return (
    <Box p={2}>
      <Typography variant="h4" gutterBottom>Admin Panel - Users & Subscriptions</Typography>
      {loading ? (
        <Typography>Loading users...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>UID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Last Payment</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Tokens</TableCell>
                <TableCell>Last Scrape</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const sub = editing[user.uid] || user.subscription || {};
                return (
                  <React.Fragment key={user.uid}>
                  <TableRow>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.uid}</TableCell>
                    <TableCell>
                      <select
                        value={sub.status || ""}
                        onChange={e => handleChange(user.uid, "status", e.target.value)}
                        style={{ width: "100%" }}
                      >
                        <option value="">Select</option>
                        {statusOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </TableCell>
                    <TableCell>
                      <select
                        value={sub.plan || ""}
                        onChange={e => handleChange(user.uid, "plan", e.target.value)}
                        style={{ width: "100%" }}
                      >
                        <option value="">Select</option>
                        {planOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </TableCell>
                    <TableCell>
                      <input
                        type="date"
                        value={sub.start_date || ""}
                        onChange={e => handleChange(user.uid, "start_date", e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="date"
                        value={sub.expiry_date || ""}
                        onChange={e => handleChange(user.uid, "expiry_date", e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        type="date"
                        value={sub.last_payment || ""}
                        onChange={e => handleChange(user.uid, "last_payment", e.target.value)}
                        style={{ width: "100%" }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={sub.notes || ""}
                        onChange={e => handleChange(user.uid, "notes", e.target.value)}
                        size="small"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={sub.tokens || 0}
                        onChange={e => handleChange(user.uid, "tokens", e.target.value)}
                        size="small"
                        fullWidth
                        inputProps={{ min: 0 }}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <b>Keyword:</b> {user.latestScrape.keyword || "-"}<br/>
                        <b>Location:</b> {user.latestScrape.location || "-"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleSave(user.uid)}
                      >
                        Save
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={() => fetchHistory(user.uid)}
                      >
                        View History
                      </Button>
                      <Button
                        variant="outlined"
                        color="info"
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={() => fetchScrapeHistory(user.uid)}
                      >
                        View Scrape History
                      </Button>
                    </TableCell>
                  </TableRow>
                  {showHistoryFor === user.uid && history[user.uid] && (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Subscription History</Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>ID</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Plan</TableCell>
                              <TableCell>Start Date</TableCell>
                              <TableCell>Expiry Date</TableCell>
                              <TableCell>Last Payment</TableCell>
                              <TableCell>Tokens</TableCell>
                              <TableCell>Notes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {history[user.uid].map((h) => (
                              <TableRow key={h.id}>
                                <TableCell>{h.id}</TableCell>
                                <TableCell>{h.status}</TableCell>
                                <TableCell>{h.plan}</TableCell>
                                <TableCell>{h.start_date}</TableCell>
                                <TableCell>{h.expiry_date}</TableCell>
                                <TableCell>{h.last_payment}</TableCell>
                                <TableCell>{h.tokens}</TableCell>
                                <TableCell>{h.notes}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  )}
                  {showScrapeFor === user.uid && scrapeHistory[user.uid] && (
                    <TableRow>
                      <TableCell colSpan={11}>
                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Scrape History</Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>ID</TableCell>
                              <TableCell>Keyword</TableCell>
                              <TableCell>Location</TableCell>
                              <TableCell>Scraped At</TableCell>
                              <TableCell>Export</TableCell>
                              <TableCell>View Data</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {scrapeHistory[user.uid].map((h, idx) => (
                              <React.Fragment key={h.id}>
                                <TableRow>
                                  <TableCell>{h.id}</TableCell>
                                  <TableCell>{h.keyword}</TableCell>
                                  <TableCell>{h.location}</TableCell>
                                  <TableCell>{h.scraped_at}</TableCell>
                                  <TableCell>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => downloadCSVFromResults(h.results, `${user.email}_scrape_${idx+1}.csv`)}
                                      disabled={!h.results || !h.results.length}
                                    >
                                      Export CSV
                                    </Button>
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => setExpandedScrape({ ...expandedScrape, [`${user.uid}_${h.id}`]: !expandedScrape[`${user.uid}_${h.id}`] })}
                                      disabled={!h.results || !h.results.length}
                                    >
                                      {expandedScrape[`${user.uid}_${h.id}`] ? 'Hide' : 'Show'}
                                    </Button>
                                  </TableCell>
                                </TableRow>
                                {expandedScrape[`${user.uid}_${h.id}`] && h.results && h.results.length > 0 && (
                                  <TableRow>
                                    <TableCell colSpan={6}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow>
                                            <TableCell>Company Name</TableCell>
                                            <TableCell>Email(s)</TableCell>
                                            <TableCell>Phone(s)</TableCell>
                                            <TableCell>Website</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {h.results.map((lead, i) => (
                                            <TableRow key={i}>
                                              <TableCell>{lead.company_name}</TableCell>
                                              <TableCell>{lead.emails}</TableCell>
                                              <TableCell>{lead.phones}</TableCell>
                                              <TableCell>{lead.website}</TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            ))}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Admin;